import { isEmpty, omit, pick } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { setLoadingStarted } from 'redux/app/actions';
import { loadAsset } from 'redux/assets/actions';

/**
 * Depending on a user's path, they may have incomplete data for an asset.
 * Specifically, information concerning campaigns and warranties is to heavy
 * on the server to load for more than one case, asset, or service request.
 * The AssetsWidget requires this data to display correctly. This component
 * validates that the assetInfo contains the required data, and dispatches a
 * request for the complete information if it does not.
 *
 * @class WithAssetDetailRequestOnlyFields
 * @extends {Component}
 */
export class WithAssetDetailRequestOnlyFields extends Component {
  static propTypes = {
    assetInfo: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
    children: PropTypes.func.isRequired,
    loadAssetInfo: PropTypes.func.isRequired,
    setLoadingStarted: PropTypes.func.isRequired,
  };

  componentWillMount() {
    const { assetInfo } = this.props;
    this.validateOrLoadAssetInfo(assetInfo);
  }

  componentWillReceiveProps(nextProps) {
    const { assetInfo } = nextProps;
    this.validateOrLoadAssetInfo(assetInfo);
  }

  /**
   * Checks for missing fields in the current asset info object.
   * Returns true if the asset info is missing required data.
   *
   * @property {object} assetInfo The current or next assetInfo object
   * @returns Boolean indicating if the assetInfo should be reloaded.
   * @memberof Container
   */
  shouldLoadAssetInfo = assetInfo => isEmpty(
    pick(
      assetInfo,
      [
        'campaigns',
        'campaignsStatus',
        'countWarranties',
        'warrantyStatus',
      ],
    ),
  )

  /**
   * Depending on the user's path some information may not be present
   * in the Asset Info. Validates the asset info. Loads/ reloads if necessary.
   *
   * @param {object} assetInfo The current or next assetInfo object
   * @memberof Container
   */
  validateOrLoadAssetInfo = (assetInfo) => {
    if (assetInfo.id && this.shouldLoadAssetInfo(assetInfo)) {
      this.props.loadAssetInfo(assetInfo.id);
      this.props.setLoadingStarted();
    }
  }

  render() {
    // The children don't need to know about loading related props.
    const componentProps = omit(this.props, ['children', 'loadAssetInfo', 'setLoadingStarted']);
    return this.props.children(componentProps);
  }
}

/* istanbul ignore next */
const withAssetDetailRequestOnlyFields = (WrappedComponent) => {
  // Using an external component/ function as a child pattern here to enhance testability.
  const InternalComponent = props => (
    <WithAssetDetailRequestOnlyFields {...props}>
      {componentProps => (
        <WrappedComponent {...componentProps}>
          {props.children}
        </WrappedComponent>
      )}
    </WithAssetDetailRequestOnlyFields>
  );

  InternalComponent.propTypes = {
    children: PropTypes.node,
  };

  InternalComponent.defaultProps = {
    children: null,
  };

  function mapStateToProps() {
    return { };
  }

  function mapDispatchToProps(dispatch) {
    return {
      loadAssetInfo: assetId => dispatch(loadAsset({ assetId })),
      setLoadingStarted: () => dispatch(setLoadingStarted(true)),
    };
  }

  return connect(mapStateToProps, mapDispatchToProps)(InternalComponent);
};

export default withAssetDetailRequestOnlyFields;



// WEBPACK FOOTER //
// ./compositions/AssetsWidget/withAssetDetailRequestOnlyFields.js